<!--
 * @Author: AI Assistant
 * @Date: 2025-12-30
 * @Description: 歌词输入组件
 * @FilePath: /weryai-web/components/Tool/LyricsInput.vue
-->
<template>
    <div class="mt-5">
        <!-- 整合容器 -->
        <div
            class="relative w-full bg-music-item-default rounded-lg border border-transparent p-[20px] transition-colors duration-200"
            :class="{
                'border-active-border': isFocused,
                'hover:border-white/30': !isFocused,
            }"
            :style="{
                borderColor: isFocused ? '#ffffff4d' : '',
            }"
        >
            <!-- 标题和AI生成按钮 -->
            <div class="flex items-center justify-between mb-2.5">
                <label for="lyrics-textarea" class="text-[#A0A0A8] text-xs">
                    {{ t('music.lyricsContent') }}
                </label>
                <!-- AI 自动生成按钮（暂时注释）
                <button
                    @click="handleAIGenerate"
                    class="text-xs text-[#99F768] flex items-center gap-1 hover:opacity-80 transition-opacity"
                    :disabled="isGenerating"
                >
                    <svg
                        v-if="!isGenerating"
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M12 3l1.912 5.813a2 2 0 0 0 1.275 1.275L21 12l-5.813 1.912a2 2 0 0 0-1.275 1.275L12 21l-1.912-5.813a2 2 0 0 0-1.275-1.275L3 12l5.813-1.912a2 2 0 0 0 1.275-1.275L12 3z"/>
                    </svg>
                    <svg
                        v-else
                        class="animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                    >
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    AI 自动生成
                </button>
                -->
            </div>

            <!-- 输入区域 -->
            <div class="relative min-h-[120px]">
                <!-- 自定义 placeholder（支持换行） -->
                <div
                    v-show="showPlaceholder"
                    class="absolute inset-0 text-[#8A8A90] text-sm pointer-events-none whitespace-pre-line"
                >
                    <div>{{ t('music.lyricsVerse') }}</div>
                    <div>{{ t('music.enterLyrics') }}</div>
                    <div>{{ t('music.lyricsChorus') }}</div>
                </div>

                <!-- 文本输入 -->
                <textarea
                    id="lyrics-textarea"
                    ref="textareaRef"
                    v-model="lyrics"
                    :maxlength="maxLength"
                    @focus="isFocused = true"
                    @blur="isFocused = false"
                    class="w-full min-h-[120px] bg-transparent text-white text-sm resize-none border-none outline-none p-0 m-0"
                    style="box-shadow: none"
                    :aria-label="t('music.lyricsContent')"
                ></textarea>
            </div>

            <!-- 字符计数 -->
            <div class="flex justify-end mt-2">
                <span
                    class="text-xs transition-colors duration-200"
                    :class="{
                        'text-yellow-400': isNearLimit && !isAtLimit,
                        'text-red-400': isAtLimit,
                        'text-[#A0A0A8]': !isNearLimit,
                    }"
                >
                    {{ characterCount }}/{{ maxLength }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from '~/composables/useI18n'

const { t } = useI18n()

// Props
const props = withDefaults(
    defineProps<{
        placeholder?: string
        maxLength?: number
    }>(),
    {
        placeholder: '',
        maxLength: 2500,
    }
)

// 歌词内容
const lyrics = defineModel<string>('lyrics', {
    default: '',
})

// UI 状态
const isFocused = ref(false)
const isGenerating = ref(false)
const textareaRef = ref<HTMLTextAreaElement>()

// 是否显示 placeholder
const showPlaceholder = computed(() => !lyrics.value && !isFocused.value)

// 字符统计
const characterCount = computed(() => lyrics.value.length)
const isNearLimit = computed(() => characterCount.value > props.maxLength * 0.8)
const isAtLimit = computed(() => characterCount.value >= props.maxLength)

// AI 生成歌词
const handleAIGenerate = async () => {
    if (isGenerating.value) return

    isGenerating.value = true
    try {
        // TODO: 调用 AI 生成歌词 API
        console.log('AI Generate Lyrics - TODO')

        // 模拟延迟
        await new Promise(resolve => setTimeout(resolve, 1500))

        // Mock 生成结果
        lyrics.value = `[Verse 1]
穿过霓虹闪烁的街道
城市梦想在无尽夜晚
每一步都是新的惊喜
仰望星光点缀的天空

[Chorus]
我们今夜如此鲜活
在月光下尽情舞蹈
让音乐释放我们的心灵
这里就是我们的归属`
    } catch (error) {
        console.error('AI generate lyrics failed:', error)
    } finally {
        isGenerating.value = false
    }
}
</script>

<style scoped>
/* 组件样式已通过 Tailwind 内联实现 */
</style>
